/**
 * 변경 이력 페이지 — 시스템 전체 audit log 를 필터링/페이지네이션 조회.
 *
 * 사용 시나리오:
 *   - "지난 주 누가 어떤 출장 일지를 수정했나" 같은 회고
 *   - 특정 사용자의 활동 추적
 *   - 알림에서 일부만 봤을 때 전체 히스토리 확인
 *
 * 권한: 모든 인증된 사용자 접근 가능 (audit 는 투명성이 핵심).
 */
import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Paper,
  Breadcrumbs,
  Link,
  Typography,
  IconButton,
  Container,
  Stack,
  Button,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Alert,
  CircularProgress,
  TablePagination,
  useTheme,
  alpha,
} from '@mui/material';
import {
  NavigateNext as NavigateNextIcon,
  Home as HomeIcon,
  History as HistoryIcon,
  ArrowBack as ArrowBackIcon,
  Refresh as RefreshIcon,
  LightMode as LightModeIcon,
  DarkMode as DarkModeIcon,
} from '@mui/icons-material';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ThemeContext } from '../contexts/ThemeContext';
import UserProfileMenu from '../components/UserProfileMenu';
import { AuditService, AuditLogItem, AuditLogFilter } from '../services/AuditService';
import ProjectPageShell from './ProjectDashboardPage/ProjectPageShell';

const ACTION_LABEL: Record<string, string> = {
  create: '등록',
  update: '수정',
  delete: '삭제',
};

const ACTION_COLOR: Record<string, 'success' | 'info' | 'error' | 'default'> = {
  create: 'success',
  update: 'info',
  delete: 'error',
};

const ENTITY_LABEL: Record<string, string> = {
  site_project: '현장',
  site_visit_log: '출장 일지',
  site_incident: '장애 이력',
  site_schedule: '일정',
};

const ENTITY_OPTIONS = Object.keys(ENTITY_LABEL);

const formatDateTime = (iso: string): string => {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString('ko-KR', { dateStyle: 'medium', timeStyle: 'short' });
};

const AuditLogPage: React.FC = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const { mode, toggleTheme } = useContext(ThemeContext);
  // URL ?project=<id> 로 들어오면 해당 현장만 필터. 없으면 전체 (master/admin view).
  const [searchParams] = useSearchParams();
  const projectParam = searchParams.get('project');
  const projectFilter = projectParam ? Number(projectParam) : undefined;

  const [items, setItems] = useState<AuditLogItem[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [entityType, setEntityType] = useState<string>('');
  const [action, setAction] = useState<string>('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(50);

  const filter = useMemo<AuditLogFilter>(
    () => ({
      entity_type: entityType || undefined,
      action: action || undefined,
      site_project_id: projectFilter && Number.isFinite(projectFilter) ? projectFilter : undefined,
      limit: rowsPerPage,
      offset: page * rowsPerPage,
    }),
    [entityType, action, projectFilter, rowsPerPage, page],
  );

  const refresh = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const resp = await AuditService.listAuditLogs(filter);
      setItems(resp.data);
      setTotal(resp.total);
    } catch (e) {
      setError(e instanceof Error ? e.message : '조회 실패');
    } finally {
      setLoading(false);
    }
  }, [filter]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const onEntityFilterChange = (v: string) => {
    setEntityType(v);
    setPage(0);
  };
  const onActionFilterChange = (v: string) => {
    setAction(v);
    setPage(0);
  };

  const handleLinkToTarget = (item: AuditLogItem) => {
    if (!item.site_project_id) return;
    if (item.entity_type === 'site_visit_log') {
      navigate(`/project/${item.site_project_id}/journal`);
    } else if (item.entity_type === 'site_incident') {
      navigate(`/project/${item.site_project_id}/incidents`);
    } else if (item.entity_type === 'site_schedule') {
      navigate(`/project/${item.site_project_id}/schedules`);
    } else if (item.entity_type === 'site_project') {
      navigate(`/project/${item.site_project_id}`);
    }
  };

  // 본문(필터 + 테이블)을 한 덩어리로 묶어 두 가지 레이아웃에서 동일하게 재사용.
  //   - project 컨텍스트 진입(?project=...) → ProjectPageShell 로 좌측 사이드바 유지.
  //   - 글로벌 진입(쿼리 없음)               → 기존 단독 헤더 유지.
  const body = (
    <Paper variant="outlined" sx={{ p: 3, borderRadius: 2 }}>
          <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 2 }}>
            <Typography variant="h6" fontWeight={700}>
              변경 이력 ({total}건)
            </Typography>
            <Box sx={{ flex: 1 }} />
            <FormControl size="small" sx={{ minWidth: 160 }}>
              <InputLabel>대상</InputLabel>
              <Select label="대상" value={entityType} onChange={(e) => onEntityFilterChange(e.target.value)}>
                <MenuItem value="">
                  <em>전체</em>
                </MenuItem>
                {ENTITY_OPTIONS.map((e) => (
                  <MenuItem key={e} value={e}>
                    {ENTITY_LABEL[e]}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: 120 }}>
              <InputLabel>동작</InputLabel>
              <Select label="동작" value={action} onChange={(e) => onActionFilterChange(e.target.value)}>
                <MenuItem value="">
                  <em>전체</em>
                </MenuItem>
                <MenuItem value="create">등록</MenuItem>
                <MenuItem value="update">수정</MenuItem>
                <MenuItem value="delete">삭제</MenuItem>
              </Select>
            </FormControl>
            <IconButton onClick={refresh} disabled={loading} aria-label="새로고침">
              <RefreshIcon />
            </IconButton>
          </Stack>

          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
              <CircularProgress />
            </Box>
          ) : items.length === 0 ? (
            <Box sx={{ py: 6, textAlign: 'center', color: 'text.secondary' }}>
              <Typography variant="body2">조건에 맞는 변경 이력이 없습니다.</Typography>
            </Box>
          ) : (
            <>
              <TableContainer component={Paper} variant="outlined">
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>시각</TableCell>
                      <TableCell>사용자</TableCell>
                      <TableCell>대상</TableCell>
                      <TableCell>동작</TableCell>
                      <TableCell>요약</TableCell>
                      <TableCell align="right">바로가기</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {items.map((i) => (
                      <TableRow key={i.id} hover>
                        <TableCell sx={{ whiteSpace: 'nowrap' }}>{formatDateTime(i.occurred_at)}</TableCell>
                        <TableCell>{i.user_name}</TableCell>
                        <TableCell>
                          <Chip
                            label={ENTITY_LABEL[i.entity_type] ?? i.entity_type}
                            size="small"
                            variant="outlined"
                          />
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={ACTION_LABEL[i.action] ?? i.action}
                            color={ACTION_COLOR[i.action] ?? 'default'}
                            size="small"
                          />
                        </TableCell>
                        <TableCell>{i.summary}</TableCell>
                        <TableCell align="right">
                          {i.site_project_id ? (
                            <Button size="small" onClick={() => handleLinkToTarget(i)}>
                              열기
                            </Button>
                          ) : (
                            <Typography variant="caption" color="text.disabled">
                              —
                            </Typography>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
              <TablePagination
                component="div"
                count={total}
                page={page}
                onPageChange={(_, p) => setPage(p)}
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={(e) => {
                  setRowsPerPage(parseInt(e.target.value, 10));
                  setPage(0);
                }}
                rowsPerPageOptions={[20, 50, 100, 200]}
                labelRowsPerPage="페이지당"
              />
            </>
          )}
    </Paper>
  );

  // 현장 컨텍스트(?project=ID)가 있으면 좌측 사이드바 유지를 위해 공통 셸로 감쌈.
  if (projectFilter !== undefined && Number.isFinite(projectFilter)) {
    return (
      <ProjectPageShell
        projectId={projectFilter}
        activeSection="history"
        breadcrumbExtra={
          <Typography
            color="text.primary"
            sx={{ display: 'flex', alignItems: 'center', fontWeight: 700 }}
          >
            <HistoryIcon sx={{ mr: 0.5 }} fontSize="inherit" />
            변경 이력
          </Typography>
        }
        hideSettingsButton
      >
        {body}
      </ProjectPageShell>
    );
  }

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'background.default', pb: 12 }}>
      <Paper
        elevation={0}
        sx={{
          borderBottom: '1px solid',
          borderColor: 'divider',
          px: 4,
          py: 1.5,
          bgcolor: 'background.paper',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <IconButton onClick={() => navigate('/projects')} size="small" aria-label="뒤로가기">
            <ArrowBackIcon fontSize="small" />
          </IconButton>
          <Breadcrumbs separator={<NavigateNextIcon fontSize="small" />} aria-label="breadcrumb">
            <Link
              underline="hover"
              color="inherit"
              onClick={() => navigate('/projects')}
              sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer', fontWeight: 500 }}
            >
              <HomeIcon sx={{ mr: 0.5 }} fontSize="inherit" />
              현장 목록
            </Link>
            <Typography color="text.primary" sx={{ display: 'flex', alignItems: 'center', fontWeight: 700 }}>
              <HistoryIcon sx={{ mr: 0.5 }} fontSize="inherit" />
              변경 이력
            </Typography>
          </Breadcrumbs>
        </Box>

        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
          <IconButton
            onClick={toggleTheme}
            size="small"
            title={mode === 'dark' ? '라이트 모드로 전환' : '다크 모드로 전환'}
            sx={{
              color: 'text.secondary',
              '&:hover': { color: 'primary.main', bgcolor: alpha(theme.palette.primary.main, 0.1) },
            }}
          >
            {mode === 'dark' ? <LightModeIcon fontSize="small" /> : <DarkModeIcon fontSize="small" />}
          </IconButton>
          <UserProfileMenu />
        </Box>
      </Paper>

      <Container maxWidth="lg" sx={{ mt: 3 }}>
        {body}
      </Container>
    </Box>
  );
};

export default AuditLogPage;
